import { useEffect, useCallback, useState, useContext, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSocket } from './useSocket';
import { AuthContext } from '../context/AuthContext';
import { ToastContext } from '../context/ToastContext';

/**
 * Hook to manage socket events for a specific game
 * Joins the game room and sets up event listeners that invalidate React Query caches
 *
 * @param {string} gameId - The game ID to subscribe to
 * @returns {{ isConnected: boolean, connectionError: string | null, onlineUserIds: string[] }}
 */
export const useGameSocket = (gameId) => {
  const { socket, isConnected, connectionError } = useSocket();
  const queryClient = useQueryClient();
  const [onlineUserIds, setOnlineUserIds] = useState([]);
  const auth = useContext(AuthContext);
  const toast = useContext(ToastContext);
  const currentUserId = auth?.user?.id; // User model transforms _id to id

  // Use refs to always get latest values in socket handlers (avoids stale closures)
  const currentUserIdRef = useRef(currentUserId);
  const toastRef = useRef(toast);
  currentUserIdRef.current = currentUserId;
  toastRef.current = toast;

  // Invalidate query helper
  const invalidate = useCallback((queryKeys) => {
    queryKeys.forEach(key => {
      queryClient.invalidateQueries({ queryKey: key });
    });
  }, [queryClient]);

  useEffect(() => {
    if (!socket || !isConnected || !gameId) {
      return;
    }

    // Join the game room
    socket.emit('join:game', gameId);

    // Round events
    socket.on('round:created', ({ roundId }) => {
      invalidate([
        ['currentRound', gameId],
        ['rounds', gameId],
        ['game', gameId],
        ['gameState', gameId], // For combined state used by Game page
      ]);
    });

    socket.on('round:phase:changed', ({ roundId, phase, autoAdvanced }) => {
      invalidate([
        ['currentRound', gameId],
        ['round', roundId],
        ['contributionStatus', gameId],
        ['votingStatus', gameId],
        ['passage', gameId],
        ['details', gameId, roundId],
        ['gameState', gameId], // For combined state used by Game page
      ]);

      // Show toast notification for auto-advance
      if (autoAdvanced && phase === 'composition') {
        const toastCtx = toastRef.current;
        if (toastCtx) {
          setTimeout(() => {
            toastRef.current?.success('All players submitted! Moving to composition phase...');
          }, 300);
        }
      }
    });

    socket.on('round:aspect:added', ({ roundId }) => {
      invalidate([
        ['currentRound', gameId],
        ['round', roundId],
        ['scene', gameId],
        ['gameState', gameId], // For combined state used by Game page
      ]);
    });

    socket.on('round:settings:updated', ({ roundId }) => {
      // GM updated round settings (passage length or prompt) during setup phase
      invalidate([
        ['currentRound', gameId],
        ['round', roundId],
        ['gameState', gameId], // For combined state used by Game page
      ]);
    });

    socket.on('round:completed', ({ roundId }) => {
      // MVP calculated, awaiting GM Pick - no distribution yet
      invalidate([
        ['currentRound', gameId],
        ['round', roundId],
        ['roundResults', roundId],
        ['details', gameId, roundId],
        ['passages', gameId],
      ]);
    });

    socket.on('round:finalized', ({ roundId, mvpUserId, gmPickUserId, gmPickAmount, distribution }) => {
      // Distribution happens when GM Pick is selected (GM Pick gets 2nd place)
      invalidate([
        ['currentRound', gameId],
        ['round', roundId],
        ['roundResults', roundId],
        ['influence', 'me', gameId],
        ['influence', 'balances', gameId],
        ['details', gameId, roundId],
        ['passages', gameId],
        ['gameState', gameId], // For combined state used by Game page - triggers "Start New Round" view
      ]);

      // Show toasts for distribution (MVP and GM Pick)
      const userId = currentUserIdRef.current;
      const toastCtx = toastRef.current;

      if (userId && distribution && toastCtx) {
        const currentUserIdStr = userId.toString();
        const myAmount = distribution[currentUserIdStr];

        if (myAmount > 0) {
          const isMVP = mvpUserId === currentUserIdStr;
          const isGMPick = gmPickUserId === currentUserIdStr;

          setTimeout(() => {
            const latestToast = toastRef.current;
            if (isMVP) {
              latestToast.award(`You won MVP! +${myAmount} Influence`);
            } else if (isGMPick) {
              latestToast.award(`You won GM Pick (2nd place)! +${myAmount} Influence`);
            } else if (myAmount > 0) {
              latestToast.success(`+${myAmount} Influence from pot distribution`);
            }
          }, 500);
        }
      }
    });

    // Submission events
    socket.on('submission:submitted', ({ roundId }) => {
      invalidate([
        ['contributionStatus', gameId],
        ['submissions', roundId],
        ['gameState', gameId], // For Advance button color
      ]);
    });

    // Passage events
    socket.on('passage:published', ({ roundId, passageId }) => {
      invalidate([
        ['passage', gameId],
        ['currentRound', gameId],
      ]);
    });

    // Vote events
    socket.on('vote:cast', ({ roundId, voterId }) => {
      invalidate([
        ['votingStatus', gameId],
        ['gameState', gameId], // For Advance button color
      ]);
    });

    // Detail events (influence phase)
    socket.on('detail:added', ({ roundId }) => {
      invalidate([
        ['details', gameId, roundId],
        ['rejection', gameId, roundId],
        ['currentRound', gameId],
      ]);
    });

    socket.on('detail:supported', ({ roundId }) => {
      invalidate([
        ['details', gameId, roundId],
        ['rejection', gameId, roundId],
      ]);
    });

    socket.on('detail:pending', ({ roundId }) => {
      invalidate([
        ['details', gameId, roundId],
      ]);
    });

    socket.on('gm:rejection:toggled', ({ roundId }) => {
      invalidate([
        ['details', gameId, roundId],
        ['rejection', gameId, roundId],
        ['currentRound', gameId],
      ]);
    });

    // Player ready events (influence phase)
    socket.on('round:player:ready:changed', ({ roundId }) => {
      invalidate([
        ['readyStatus', gameId, roundId],
        ['gameState', gameId], // For Advance button color
      ]);
    });

    // Influence events (user-specific, but invalidate game queries)
    socket.on('influence:updated', ({ userId, balance }) => {
      invalidate([
        ['influence', 'me', gameId],
        ['influence', 'balances', gameId],
      ]);
    });

    // Presence events
    socket.on('presence:updated', ({ gameId: gId, onlineUserIds: userIds }) => {
      if (gId === gameId) {
        setOnlineUserIds(userIds);
      }
    });

    // Toast notifications from GM
    socket.on('toast:show', ({ message, type }) => {
      if (toast) {
        switch (type) {
          case 'success':
            toast.success(message);
            break;
          case 'error':
            toast.error(message);
            break;
          case 'award':
            toast.award(message);
            break;
          default:
            toast.info(message);
        }
      }
    });

    // Cleanup: leave room and remove listeners
    return () => {
      socket.emit('leave:game', gameId);
      setOnlineUserIds([]);

      socket.off('round:created');
      socket.off('round:phase:changed');
      socket.off('round:aspect:added');
      socket.off('round:settings:updated');
      socket.off('round:completed');
      socket.off('round:finalized');
      socket.off('submission:submitted');
      socket.off('passage:published');
      socket.off('vote:cast');
      socket.off('detail:added');
      socket.off('detail:supported');
      socket.off('detail:pending');
      socket.off('gm:rejection:toggled');
      socket.off('round:player:ready:changed');
      socket.off('influence:updated');
      socket.off('presence:updated');
      socket.off('toast:show');
    };
  }, [socket, isConnected, gameId, invalidate]); // currentUserId and toast accessed via refs

  return { isConnected, connectionError, onlineUserIds };
};
